import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { User, Mail, Lock, Save, ShoppingBag, Download, Star, DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
      }));
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/profile/stats`);
      setStats(response.data);
    } catch (e) {
      console.error("Failed to fetch stats");
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {};
      
      if (formData.name !== user.name) {
        updateData.name = formData.name;
      }
      
      if (formData.email !== user.email) {
        updateData.email = formData.email;
      }

      if (Object.keys(updateData).length === 0) {
        toast.info("No changes to save");
        setLoading(false);
        return;
      }

      await axios.put(`${API}/profile`, updateData);
      toast.success("Profile updated successfully");
      
      // Refresh the page to update context
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!formData.current_password || !formData.new_password) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (formData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/profile`, {
        current_password: formData.current_password,
        new_password: formData.new_password,
      });
      toast.success("Password changed successfully");
      setFormData((prev) => ({
        ...prev,
        current_password: "",
        new_password: "",
        confirm_password: "",
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="profile-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <p className="overline mb-2">SETTINGS</p>
            <h1
              className="text-3xl sm:text-4xl font-semibold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Profile Settings
            </h1>
            <p className="mt-2 text-zinc-600">
              Manage your account settings and preferences.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-4 rounded-xl border border-zinc-200">
              <ShoppingBag className="w-5 h-5 text-indigo-500 mb-2" />
              <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                {stats?.total_orders || 0}
              </p>
              <p className="text-xs text-zinc-500">Orders</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-zinc-200">
              <DollarSign className="w-5 h-5 text-green-500 mb-2" />
              <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                ${(stats?.total_spent || 0).toFixed(0)}
              </p>
              <p className="text-xs text-zinc-500">Spent</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-zinc-200">
              <Download className="w-5 h-5 text-orange-500 mb-2" />
              <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                {stats?.total_downloads || 0}
              </p>
              <p className="text-xs text-zinc-500">Downloads</p>
            </div>
            <div className="bg-white p-4 rounded-xl border border-zinc-200">
              <Star className="w-5 h-5 text-yellow-500 mb-2" />
              <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
                {stats?.total_reviews || 0}
              </p>
              <p className="text-xs text-zinc-500">Reviews</p>
            </div>
          </div>

          {/* Settings Tabs */}
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="w-full justify-start border-b border-zinc-200 bg-transparent h-auto p-0 mb-8">
              <TabsTrigger
                value="profile"
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger
                value="security"
                className="data-[state=active]:border-b-2 data-[state=active]:border-zinc-900 rounded-none bg-transparent px-4 py-3"
              >
                <Lock className="w-4 h-4 mr-2" />
                Security
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="bg-white p-6 rounded-xl border border-zinc-200">
                <h3 className="font-medium mb-6">Personal Information</h3>
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                          }
                          className="pl-10 h-11"
                          placeholder="Your name"
                          data-testid="profile-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) =>
                            setFormData({ ...formData, email: e.target.value })
                          }
                          className="pl-10 h-11"
                          placeholder="you@example.com"
                          data-testid="profile-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary flex items-center gap-2"
                      data-testid="save-profile-btn"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>

              {/* Account Info */}
              <div className="bg-white p-6 rounded-xl border border-zinc-200 mt-6">
                <h3 className="font-medium mb-4">Account Information</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Account ID</span>
                    <span className="font-mono text-xs">{user?.id?.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Account Type</span>
                    <span>{user?.is_admin ? "Administrator" : "Standard User"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Member Since</span>
                    <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</span>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <div className="bg-white p-6 rounded-xl border border-zinc-200">
                <h3 className="font-medium mb-6">Change Password</h3>
                <form onSubmit={handleChangePassword} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="current_password">Current Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="current_password"
                        type="password"
                        value={formData.current_password}
                        onChange={(e) =>
                          setFormData({ ...formData, current_password: e.target.value })
                        }
                        className="pl-10 h-11"
                        placeholder="••••••••"
                        data-testid="current-password-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="new_password">New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="new_password"
                          type="password"
                          value={formData.new_password}
                          onChange={(e) =>
                            setFormData({ ...formData, new_password: e.target.value })
                          }
                          className="pl-10 h-11"
                          placeholder="••••••••"
                          data-testid="new-password-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm_password">Confirm New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                        <Input
                          id="confirm_password"
                          type="password"
                          value={formData.confirm_password}
                          onChange={(e) =>
                            setFormData({ ...formData, confirm_password: e.target.value })
                          }
                          className="pl-10 h-11"
                          placeholder="••••••••"
                          data-testid="confirm-password-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary flex items-center gap-2"
                      data-testid="change-password-btn"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full spinner"></div>
                      ) : (
                        <Lock className="w-4 h-4" />
                      )}
                      Change Password
                    </button>
                  </div>
                </form>
              </div>

              {/* Danger Zone */}
              <div className="bg-white p-6 rounded-xl border border-red-200 mt-6">
                <h3 className="font-medium text-red-600 mb-4">Danger Zone</h3>
                <p className="text-sm text-zinc-600 mb-4">
                  Once you log out, you'll need to sign in again to access your account.
                </p>
                <button
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  data-testid="logout-btn"
                >
                  Sign Out of Account
                </button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
}
